import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Briefcase, Sparkles, Info, HelpCircle, MessageCircle } from "lucide-react";
import { LANDING } from "@/constants/testIds";

// FAQ curado com terminologia BR aprovada:
// - "Comissão Alta" (não "take rate")
// - "Pagamento Protegido" (não "escrow")
// - Modelo de split de pagamentos explícito (evita bitributação)
const FAQ_ITEMS = [
  // ===== MARCAS =====
  {
    id: 1,
    cat: "brand",
    q: "Como funciona o Pagamento Protegido?",
    a: "Quando você contrata, o valor sai do seu cartão/PIX e vai direto pro cofre protegido no gateway certificado — não pra conta do criador nem da Alta. Depois que o criador entrega o conteúdo, você tem até 7 dias pra revisar e aprovar. Só nesse momento o dinheiro é liberado. Se o criador não entregar dentro do prazo combinado, você recebe reembolso automático.",
  },
  {
    id: 2,
    cat: "brand",
    q: "E se eu não gostar do resultado?",
    a: "Todo contrato inclui 2 rodadas de revisão gratuitas por padrão (você pode aumentar). Se após as revisões você ainda achar que a entrega não atende ao briefing, pode abrir uma disputa — nossa equipe analisa em até 48h e decide se libera reembolso parcial ou total.",
  },
  {
    id: 3,
    cat: "brand",
    q: "Quanto custa no total pra minha marca?",
    a: "Você paga exatamente o valor combinado com o criador — nada além disso. Sem mensalidade, sem taxa de cadastro, sem letra miúda. O pagamento é dividido automaticamente via split no gateway: 85% vai direto pro criador e 15% vai pra Alta (nossa Comissão Alta). Ou seja, a marca é a única fonte pagadora do criador, e cada parte recebe sua parcela de forma independente. Se você é uma das 20 marcas fundadoras, a Comissão Alta é 0% nos primeiros 6 meses — você paga só o cachê do criador.",
  },
  {
    id: 4,
    cat: "brand",
    q: "Como sei se o criador é bom?",
    a: "Cada criador tem: histórico completo de campanhas concluídas, avaliação de outras marcas, portfólio com trabalhos anteriores, dados reais de audiência (Instagram/TikTok verificados) e nosso Índice de Compatibilidade 0-100 que calcula a aderência dele ao seu nicho e briefing. Você aprova o criador antes de qualquer produção começar.",
  },

  // ===== CRIADORES =====
  {
    id: 5,
    cat: "creator",
    q: "Preciso ter quantos seguidores pra me cadastrar?",
    a: "A partir de 1.000 seguidores você já pode se cadastrar. Aqui o que importa mais é engajamento real e a qualidade do seu conteúdo, não só o tamanho da audiência. Micro-influenciadores (10k-100k) e nano (1k-10k) são super valorizados por marcas que buscam conversão real.",
  },
  {
    id: 6,
    cat: "creator",
    q: "Quando eu recebo o pagamento?",
    a: "No momento em que a marca aprova sua entrega, o valor cai imediatamente na sua conta bancária via PIX — sem D+30, sem espera, sem repasse quinzenal. É o modelo mais rápido do mercado brasileiro.",
  },
  {
    id: 7,
    cat: "creator",
    q: "Preciso pagar alguma coisa pra usar a plataforma?",
    a: "Zero. Cadastro grátis pra sempre, sem mensalidade, sem taxa de saque, sem taxa escondida. A Alta só ganha quando você ganha (15% de Comissão Alta sobre cada campanha concluída, retido automaticamente pelo gateway).",
  },
  {
    id: 8,
    cat: "creator",
    q: "Como faço com imposto de renda?",
    a: "Todo mês a plataforma gera pra você um Relatório de Rendimentos em PDF com todas as campanhas concluídas: contratante (nome + CNPJ da marca), data, valor bruto, Comissão Alta retida e valor líquido recebido. Importante: a fonte pagadora é a marca contratante — não a Alta Creators. A Alta atua como intermediadora tecnológica, e o pagamento vai da marca diretamente pra você via split de pagamentos, sem passar pela nossa conta bancária. Você declara como MEI ou pessoa física normalmente, listando cada marca como pagador. Sem bitributação, sem retenção na fonte pela Alta.",
  },

  // ===== GERAL =====
  {
    id: 9,
    cat: "general",
    q: "É seguro? Meus dados estão protegidos?",
    a: "Sim. A plataforma é 100% LGPD-compliant, com criptografia HTTPS de ponta a ponta, sessões protegidas com JWT (você pode revogar logins de outros dispositivos a qualquer momento) e senhas em bcrypt. Não vendemos nem compartilhamos seus dados.",
  },
  {
    id: 10,
    cat: "general",
    q: "Preciso emitir nota fiscal?",
    a: "A relação de prestação de serviço é entre criador e marca — a Alta apenas conecta e intermedia via tecnologia. Portanto: o criador emite NF direto pra marca contratante (como MEI, PJ ou pessoa física acima dos limites da Receita Federal). A Alta emite sua própria NF de comissão (15%) separadamente pra marca — apenas sobre o valor de intermediação. Cada NF fica armazenada no contrato dentro da plataforma. Sem duplicidade, sem sobreposição de tributos.",
  },
  {
    id: 11,
    cat: "general",
    q: "E se a marca ou o criador sumir depois do contrato?",
    a: "Impossível: o pagamento fica no cofre protegido no gateway, não em posse de nenhuma das partes. Se o criador sumir sem entregar, a marca recebe reembolso automático após o prazo. Se a marca sumir sem aprovar, o criador é liberado automaticamente após 30 dias (prazo de retenção configurável). Todo movimento gera certificado digital verificável.",
  },
  {
    id: 12,
    cat: "general",
    q: "Como o dinheiro flui? A Alta é fonte pagadora?",
    a: "Não. A Alta atua como intermediadora tecnológica — nunca custodiamos seu dinheiro. Quando a marca paga uma campanha, o valor entra num gateway certificado (PCI-DSS + Bacen) que faz o split simultâneo: 85% direto na conta bancária do criador (com hold ativado até a aprovação da marca — é assim que o Pagamento Protegido funciona: no gateway, não na nossa infra) e 15% direto pra Alta (Comissão Alta). Benefícios: zero bitributação (cada CNPJ é tributado só pelo que efetivamente recebe), a marca continua sendo a única fonte pagadora do criador, e o dinheiro está sempre protegido por certificação PCI-DSS nível 1 (mesmo padrão dos grandes bancos).",
  },
  {
    id: 13,
    cat: "general",
    q: "Como falo com o suporte?",
    a: "Nosso suporte responde por chat na plataforma, e-mail e WhatsApp em até 4 horas úteis (segunda a sexta, 9h-19h). Para disputas ou casos críticos, temos SLA de resposta em até 48h.",
  },
];

const TABS = [
  { id: "all", label: "Todas", icon: HelpCircle, testId: LANDING.faqTabAll },
  { id: "brand", label: "Para Marca", icon: Briefcase, testId: LANDING.faqTabBrand },
  { id: "creator", label: "Para Criador", icon: Sparkles, testId: LANDING.faqTabCreator },
  { id: "general", label: "Geral", icon: Info, testId: LANDING.faqTabGeneral },
];

const CAT_META = {
  brand: { icon: Briefcase, color: "text-[#DC2626]", label: "Marca" },
  creator: { icon: Sparkles, color: "text-[#DC2626]", label: "Criador" },
  general: { icon: Info, color: "text-[#DC2626]", label: "Geral" },
};

export default function LandingFAQ() {
  const [activeTab, setActiveTab] = useState("all");
  const filtered = activeTab === "all" ? FAQ_ITEMS : FAQ_ITEMS.filter((i) => i.cat === activeTab);

  return (
    <section
      className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E8E6E1]"
      data-testid={LANDING.faqSection}
    >
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
          <HelpCircle className="w-3 h-3" /> Perguntas frequentes
        </div>
        <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1]">
          Tudo o que você precisa<br />saber antes de <span className="warm-gradient bg-clip-text text-transparent">começar</span>
        </h3>
      </div>

      {/* Tabs de filtro */}
      <div className="flex items-center justify-center gap-2 flex-wrap mb-8">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              data-testid={tab.testId}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                active
                  ? "bg-[#DC2626] text-white shadow-soft-red"
                  : "bg-white border border-[#E8E6E1] text-[#4A4A4A] hover:border-[#DC2626]/30 hover:text-[#DC2626]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Accordion */}
      <Card className="bg-white border border-[#E8E6E1] p-2 md:p-4 shadow-soft-red">
        <Accordion type="single" collapsible className="w-full">
          {filtered.map((item) => {
            const meta = CAT_META[item.cat];
            const Icon = meta.icon;
            return (
              <AccordionItem
                key={item.id}
                value={`faq-${item.id}`}
                className="border-b border-[#F5F5F1] last:border-0 px-3 md:px-5"
                data-testid={`${LANDING.faqItem}-${item.id}`}
              >
                <AccordionTrigger className="text-left hover:no-underline py-5 group">
                  <div className="flex items-start gap-3 flex-1 min-w-0 pr-3">
                    <div className="w-8 h-8 rounded-lg bg-[#FFF1F1] border border-[#FEE2E2] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                      <Icon className={`w-4 h-4 ${meta.color}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-[#9B9B9B] font-semibold mb-0.5">
                        {meta.label}
                      </div>
                      <div className="font-display font-semibold text-base md:text-lg text-[#0A0A0A] leading-tight">
                        {item.q}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-[#4A4A4A] pb-6 pl-[52px] pr-2 leading-relaxed text-sm md:text-base">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Card>

      {/* CTA rodapé — Fale com a gente */}
      <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
        <div className="text-sm text-[#6B6B6B]">Ainda tem dúvida?</div>
        <a
          href="mailto:suporte@altacreators.com.br"
          className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-4 py-2 text-sm text-[#0A0A0A] hover:border-[#DC2626]/40 hover:text-[#DC2626] transition-all font-medium"
          data-testid="faq-support-email"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Fale com o suporte
        </a>
      </div>
    </section>
  );
}
